import type { BusSeatFloorView, BusSegmentPick } from '../models.js';
import { busSeatDeck, busSeatsShort, busSeatTraits, type BusSeatTraits } from './bus_seat_rules.js';

/** O que o viajante declarou nas Preferências de ônibus e vale para a planta:
 *  a posição na fileira, o terço do ônibus e sentar junto de quem viaja com
 *  ele. `null` = sem preferência. */
export type BusSeatPreference = {
  readonly row: 'window' | 'aisle' | null;
  readonly area: 'front' | 'middle' | 'back' | null;
  readonly together: boolean;
};

/** O que uma poltrona precisa atender — a preferência sem o "juntos". */
type Criteria = Pick<BusSeatPreference, 'row' | 'area'>;

const ANY: Criteria = { row: null, area: null };

/** Uma poltrona livre com o lugar dela na planta (os traços de
 *  `busSeatTraits`). */
type Slot = Pick<BusSeatTraits, 'row' | 'lane' | 'side' | 'window' | 'aisle' | 'area'> & {
  readonly code: string;
  readonly floor: number;
};

/** As poltronas livres de um trecho, da frente para o fundo, andar por andar.
 *  Janela, corredor e terço vêm de `busSeatTraits`: sem colunas (o deque
 *  `estimated`), nenhuma é janela nem corredor. */
function freeSlots(floors: readonly BusSeatFloorView[]): Slot[] {
  return floors.flatMap((floorView, floor) => {
    const deck = busSeatDeck(floorView);
    const traits = busSeatTraits(deck);
    const rows = Math.max(0, ...deck.lanes.map((lane) => lane.cells.length));
    return Array.from({ length: rows }, (_, row) =>
      deck.lanes.flatMap((laneView) => {
        const seat = laneView.cells[row];
        const trait = seat && !seat.occupied ? traits.get(seat.code) : undefined;
        if (!seat || !trait) return [];
        const { lane, side, window, aisle, area } = trait;
        return [{ code: seat.code, floor, row, lane, side, window, aisle, area }];
      }),
    ).flat();
  });
}

/** A poltrona atende o critério. */
function fits(slot: Slot, criteria: Criteria): boolean {
  const row = criteria.row === null || (criteria.row === 'window' ? slot.window : slot.aisle);
  const area = criteria.area === null || slot.area === criteria.area;
  return row && area;
}

/** Os critérios do mais exigente ao mais solto: sem o terço do ônibus, por fim
 *  sem a posição na fileira. */
function relaxations(criteria: Criteria): Criteria[] {
  const steps = [criteria, { ...criteria, area: null }, ANY];
  return steps.filter(
    (step, index) =>
      steps.findIndex((other) => other.row === step.row && other.area === step.area) === index,
  );
}

/** Os assentos de uma fileira de um lado do corredor, na ordem da planta. */
function sideGroups(slots: readonly Slot[]): Slot[][] {
  const groups = new Map<string, Slot[]>();
  for (const slot of slots) {
    const key = `${String(slot.floor)}:${String(slot.row)}:${String(slot.side)}`;
    groups.set(key, [...(groups.get(key) ?? []), slot]);
  }
  return [...groups.values()];
}

/** Um par cabe num lado de fileira: dois viajantes só ficam "juntos" lado a
 *  lado na MESMA fileira. Três ou mais seguem pelas fileiras de trás. */
const PAIR = 2;

/** O bloco que começa no lado de fileira `start`: o próprio lado (até um par)
 *  ou, para um grupo maior, os lados das fileiras seguidas — mesmo lado e andar.
 *  O grupo maior só começa num lado com um par livre: os dois primeiros
 *  viajantes ficam lado a lado, e não um na frente do outro. */
function blockFrom(groups: readonly Slot[][], start: number, count: number): Slot[] {
  const opening = groups[start] ?? [];
  const first = opening[0];
  if (!first) return [];
  if (count <= PAIR) return opening.slice(0, count);
  if (opening.length < PAIR) return [];
  const block: Slot[] = [];
  let at = first.row;
  for (const next of groups.slice(start)) {
    const head = next[0];
    if (!head || head.floor !== first.floor || head.side !== first.side) continue;
    if (head.row !== at) break;
    block.push(...next);
    at += 1;
    if (block.length >= count) break;
  }
  // Fileiras inteiras: quem corta no tamanho do grupo é `togetherBlock`, depois
  // de pôr a posição preferida na frente de cada fileira.
  return block;
}

/**
 * Um bloco de `count` poltronas lado a lado: o primeiro lado de fileira que leva
 * o grupo inteiro e tem uma poltrona que atende o critério.
 */
function togetherBlock(
  slots: readonly Slot[],
  count: number,
  criteria: Criteria,
): Slot[] | undefined {
  const groups = sideGroups(slots);
  for (const start of groups.keys()) {
    const block = blockFrom(groups, start, count);
    if (block.length < count) continue;
    // Fileira por fileira, da frente para o fundo, e DENTRO de cada fileira a
    // poltrona que atende o critério primeiro: o primeiro da lista (o
    // solicitante, quando viaja) fica nela e o segundo, ao lado dele.
    const chosen = [...block]
      .sort((a, b) => a.row - b.row || Number(fits(b, criteria)) - Number(fits(a, criteria)))
      .slice(0, count);
    if (chosen.some((slot) => fits(slot, criteria))) return chosen;
  }
  return undefined;
}

/** Uma poltrona por viajante: primeiro as que atendem tudo, depois as que
 *  atendem o critério mais solto seguinte — da frente para o fundo em cada um. */
function oneByOne(slots: readonly Slot[], count: number, steps: readonly Criteria[]): Slot[] {
  const rank = (slot: Slot) => {
    const index = steps.findIndex((step) => fits(slot, step));
    return index === -1 ? steps.length : index;
  };
  return [...slots].sort((a, b) => rank(a) - rank(b)).slice(0, count);
}

/** O critério que a planta consegue conferir: sem colunas não há janela nem
 *  corredor, e a posição na fileira sai do critério. */
function usableCriteria(preference: BusSeatPreference, slots: readonly Slot[]): Criteria {
  const classified = slots.some((slot) => slot.window || slot.aisle);
  return {
    row: classified ? preference.row : null,
    area: preference.area,
  };
}

/**
 * A poltrona de cada viajante num trecho, a partir das Preferências de ônibus —
 * só uma SUGESTÃO marcada na planta: quem confirma é o `Selecionar`. Sem
 * preferência que a planta consiga conferir (e sentar juntos sem grupo), sem
 * poltrona para todos ou sem bloco para o grupo sem outro critério, não marca
 * nada (`undefined`).
 *
 * Sentar juntos (com 2+ viajantes) vem primeiro: um bloco lado a lado que
 * atenda o critério, soltando um critério por vez (o terço do ônibus, depois a
 * posição na fileira); sem bloco, cada um na poltrona que
 * melhor atende. Da frente para o fundo, sempre.
 */
export function preselectBusSeats(
  floors: readonly BusSeatFloorView[],
  travelerIds: readonly string[],
  preference: BusSeatPreference,
): BusSegmentPick | undefined {
  const count = travelerIds.length;
  if (count === 0 || busSeatsShort(floors, count)) return undefined;
  const slots = freeSlots(floors);
  const criteria = usableCriteria(preference, slots);
  const hasCriteria = criteria.row !== null || criteria.area !== null;
  const group = preference.together && count > 1;
  if (!hasCriteria && !group) return undefined;
  const steps = relaxations(criteria);
  const block = group
    ? steps.reduce<Slot[] | undefined>(
        (found, step) => found ?? togetherBlock(slots, count, step),
        undefined,
      )
    : undefined;
  const chosen = block ?? (hasCriteria ? oneByOne(slots, count, steps) : undefined);
  if (!chosen || chosen.length < count) return undefined;
  return {
    kind: 'seats',
    seats: Object.fromEntries(travelerIds.map((id, index) => [id, chosen[index]?.code ?? ''])),
  };
}
